import { enumeratePeriodsBetweenDates } from 'utils/date.js';
import { sortDescBy, guid } from 'utils/functional.js';
import { debounce } from 'lodash';
import LocalStorage from 'Core/commons/LocalStorage';

/**
 * Returns the number of activity specific in a specific period
 * if period is not found, return 0 activity for the period
 *
 * @param {String} mode - mode of visualization, needed if mode is week for date format
 * @param {String} aggregationsByPeriod - data received from back
 * @param {String} searchActivity - name of specific activity
 *
 * @return {function} callback function execute by .map()
 */
const getCountByActivity = (mode, aggregationsByPeriod, searchActivity) => (periodBetweenDates) => {
    const findInPeriod = getFindInPeriod(mode, aggregationsByPeriod, periodBetweenDates);

    return findInPeriod
        ? Object.keys(findInPeriod)
            .filter((activity) => activity.startsWith(searchActivity))
            .map((activity) => findInPeriod[activity])
            .reduce((total, curr) => total + curr, 0)
        : 0;
};
/**
 * Returns the number of label specific in a specific period and specific activity
 * if period is not found, return 0 label for the period and activity
 *
 * @param {String} mode - mode of visualization, needed if mode is week for date format
 * @param {String} aggregationsByPeriod - data received from back
 * @param {String} searchActivity - name of specific activity
 * @param {String} idLabel - name of specific label
 *
 * @return {function} callback function execute by .map()
 */
const getCountByLabel =
    (mode, aggregationsByPeriod, idLabel, searchActivity) => (periodBetweenDates) => {
        const findInPeriod = getFindInPeriod(mode, aggregationsByPeriod, periodBetweenDates);

        const findByActivity = Object.keys(findInPeriod)
            .filter((activity) => activity.startsWith(searchActivity))
            .map((activity) => findInPeriod[activity])[0];

        //check if activity is defined to avoid error when we get index else we return 0
        if (findByActivity) {
            return findByActivity[idLabel] ? findByActivity[idLabel] : 0;
        } else {
            return 0;
        }
    };

function getFindInPeriod(mode, aggregationsByPeriod, periodBetweenDates) {
    const safePeriod =
        mode === 'weeks' ? moment(periodBetweenDates).format('W') : periodBetweenDates;

    const safeAggregations = Object.entries(aggregationsByPeriod).reduce(
        (obj, [key, value]) =>
            mode === 'weeks'
                ? { ...obj, [moment(key).format('W')]: value }
                : { ...obj, [key]: value },
        {},
    );

    return safeAggregations[safePeriod];
}

/**
 * Return the label displaying to the x Axis for a specific period
 *
 * @param {String} mode - mode of visualization, needed for display label with wanted format
 *
 * @return {function} callback function execute by .map()
 */
const getLabelByMode = (mode) => (periodBetweenDates) => {
    switch (mode) {
        case 'days':
            return window.sellsyapp.config.globalProperties.$filters.dateFormatter(
                periodBetweenDates,
                'SLASHED_DATE',
                false,
                false,
            );
        case 'months':
            return window.sellsyapp.config.globalProperties.$filters.dateFormatter(
                periodBetweenDates,
                'SHORT_MONTH',
            ); // .slice(0,3) for the first 3 characters
        case 'weeks':
            return window.i18n.__(`Semaine ${moment(periodBetweenDates).format('W')}`);
        default:
            return periodBetweenDates;
    }
};

const localstorage = new LocalStorage('report', 'activity-report-settings');

/**
 * Return aggregations Object with the additon of total key (total of activities for each aggregations)
 * @param {*} aggregations
 * @returns
 */
const addTotalActivities = (aggregations) =>
    Object.fromEntries(
        Object.entries(aggregations).map(([k, { total, ...v }]) => [
            k,
            { ...v, total: Object.values(v).reduce((r, v) => r + v, 0) },
        ]),
    );

export default {
    namespaced: true,
    state() {
        return {
            embed: 'day',
            status: 'pending',
            filters: {
                due_date: {
                    start: moment().startOf('month'),
                    end: moment().endOf('month'),
                },
                authors: null,
            },
            rows: [],
            view: 'chart',
            type: 'all',
            chartData: null,
            staffs: null,
            periodSelected: 'day',
            periods: [
                {
                    id: 'day',
                    label: window.i18n.__('Jour'),
                },
                {
                    id: 'week',
                    label: window.i18n.__('Semaine'),
                },
                {
                    id: 'month',
                    label: window.i18n.__('Mois'),
                },
            ],
            activities: [],
            activitySelected: 'task',
        };
    },
    getters: {
        status: (state) => {
            return state.status;
        },
        embed: (state) => {
            return state.embed;
        },
        filters: (state) => {
            return state.filters;
        },
        rows: (state) => {
            return state.rows;
        },
        view: (state) => {
            return state.view;
        },
        type: (state) => {
            return state.type;
        },
        chartData: (state) => {
            return state.chartData;
        },
        periodSelected: (state) => {
            return state.periodSelected;
        },
        periods: (state) => {
            return state.periods;
        },
        authors: (state) => {
            return !!state.filters.authors ? state.filters.authors : [];
        },
        staffs: (state) => {
            return state.staffs;
        },
        staffGroups: (state) => {
            return _.uniqBy(
                state.staffs
                    .filter((staff) => !!staff.groups)
                    .map((staff) => staff.groups)
                    .flat(),
                'id',
            );
        },
        activities: (state) => {
            return state.activities;
        },
        activitySelected: (state) => {
            return state.activitySelected;
        },
    },
    mutations: {
        setStatus(state, payload) {
            state.status = payload;
        },
        setEmbed(state, payload) {
            state.embed = payload;
        },
        setPeriodSelected(state, payload) {
            state.periodSelected = payload;
        },
        setFilters(state, payload) {
            state.filters = payload;
        },
        setRows(state, payload) {
            state.rows = payload;
        },
        setView(state, payload) {
            state.view = payload;
        },
        setType(state, payload) {
            state.type = payload;
        },
        setChartData(state, payload) {
            state.chartData = payload;
        },
        setStaffs(state, payload) {
            state.staffs = payload;
        },
        setActivities(state, payload) {
            state.activities = payload;
        },
        setActivitySelected(state, payload) {
            state.activitySelected = payload;
        },
    },
    actions: {
        updateEmbed({ commit, dispatch }, embed) {
            commit('setEmbed', embed);
            dispatch('saveOnBrowser');
            dispatch('loadReportData');
        },
        updatePeriodSelected({ commit, dispatch, getters }, period) {
            getters.type === 'staff' || getters.type === 'label'
                ? commit('setEmbed', 'author')
                : commit('setEmbed', period);
            commit('setPeriodSelected', period);
            dispatch('saveOnBrowser');
            dispatch('loadReportData');
        },
        updateFilters({ commit, dispatch }, filters) {
            commit('setFilters', filters);
            dispatch('saveOnBrowser');
            dispatch('loadReportData');
        },
        updateRows({ commit, getters }, { aggregations, intervals, mode }) {
            /**
             * Determine total activities for one group by all children staffs activity
             */
            const getTotalForGroup = (group) => {
                return {
                    tasks: group.children
                        .map((staff) => staff.tasks)
                        .reduce((acc, val) => acc + val, 0),
                    calls: group.children
                        .map((staff) => staff.calls)
                        .reduce((acc, val) => acc + val, 0),
                    emails: group.children
                        .map((staff) => staff.emails)
                        .reduce((acc, val) => acc + val, 0),
                    events: group.children
                        .map((staff) => staff.events)
                        .reduce((acc, val) => acc + val, 0),
                    total: group.children
                        .map((staff) => staff.total)
                        .reduce((acc, val) => acc + val, 0),
                };
            };
            /**
             * Format staff with own activities total
             */
            const getStaffEventsFeed = (staff) => {
                const staffWithActivity = rows.find((row) => row.staff.id === staff.id);
                return {
                    ...staff,
                    avatar: {
                        type: staff.avatar ? 'picture' : 'initials',
                        value: staff.avatar
                            ? staff.avatar
                            : staff.firstname.charAt(0) + staff.lastname.charAt(0) || ' ',
                        class: null,
                    },
                    id: `${staff.id}_${guid()}`,
                    period: staffWithActivity ? staffWithActivity.period : null,
                    tasks: staffWithActivity ? staffWithActivity.tasks : 0,
                    calls: staffWithActivity ? staffWithActivity.calls : 0,
                    emails: staffWithActivity ? staffWithActivity.emails : 0,
                    events: staffWithActivity ? staffWithActivity.events : 0,
                    total: staffWithActivity ? staffWithActivity.total : 0,
                };
            };
            /**
             * Get total count by activity for each table rows
             * @param key: date|staffId
             */
            let rows = intervals.map((key) => {
                const staff =
                    getters.type === 'staff'
                        ? getters.staffs.find((staff) => staff.id === +_.last(key.split('_')))
                        : null;
                const row = {
                    period: getLabelByMode(mode)(key),
                    tasks: getCountByActivity(mode, aggregations, 'task')(key),
                    calls: getCountByActivity(mode, aggregations, 'phonecall')(key),
                    emails: getCountByActivity(mode, aggregations, 'email')(key),
                    events: getCountByActivity(mode, aggregations, 'event')(key),
                    staff: !!staff
                        ? {
                            ...staff,
                            avatar: {
                                type: staff.avatar ? 'picture' : 'initials',
                                value: staff.avatar
                                    ? staff.avatar
                                    : staff.firstname.charAt(0) + staff.lastname.charAt(0) || ' ',
                                class: null,
                            },
                        }
                        : null,
                };
                row.total = row.tasks + row.calls + row.emails + row.events;

                return row;
            });

            /**
             * On staff view, listing need to display staffs groups. We need to build them with all corp staffs
             */
            if (getters.type === 'staff') {
                const groupRows = getters.staffGroups.map((mainGroup) => {
                    let group = {
                        ...mainGroup,
                        id: 'group_' + mainGroup.id,
                        children: getters.staffs
                            .filter(
                                (staff) =>
                                    !!staff.groups &&
                                    staff.groups.map((grp) => grp.id).includes(mainGroup.id),
                            )
                            .map((staff) => {
                                return getStaffEventsFeed(staff);
                            })
                            .sort((a, b) => {
                                return b.total - a.total;
                            }),
                    };

                    group = {
                        ...group,
                        ...getTotalForGroup(group),
                    };

                    return group;
                });

                /**
                 * Add fake group for staffs without group
                 */
                const noGroupStaffs = getters.staffs
                    .filter((staff) => _.isNil(staff.groups))
                    .map((staff) => getStaffEventsFeed(staff));
                groupRows.push({
                    id: null,
                    name: window.i18n.__('Sans groupe'),
                    children: noGroupStaffs,
                    ...getTotalForGroup({ children: noGroupStaffs }),
                });

                /**
                 * Replace basic aggregation staff rows by groups rows
                 */
                rows = groupRows.sort((a, b) => {
                    return b.total - a.total;
                });
            }

            commit('setRows', rows);
        },
        updateView({ commit }, view) {
            const views = ['listing', 'chart'];
            if (!views.includes(view)) return;

            commit('setView', view);
        },
        updateType({ commit, dispatch, getters }, type) {
            const types = ['all', 'staff', 'label'];
            if (!types.includes(type)) return;

            commit('setStatus', 'pending');
            if (type === 'staff' || type === 'label') {
                commit('setEmbed', 'author');
            } else {
                commit('setEmbed', getters.periodSelected);
            }
            commit('setType', type);
            dispatch('saveOnBrowser');
            dispatch('loadReportData');
        },
        updateActivitySelected({ commit, dispatch, getters }, activity) {
            commit('setActivitySelected', activity);
            dispatch('loadReportData');
        },
        /**
         * Update data of chart view
         */
        updateChartData({ commit, getters }, { aggregations, intervals, mode }) {
            let datasets = [];
            if (getters.type === 'label') {
                for (const item of getters.activities[getters.activitySelected]) {
                    const data = intervals.map(
                        getCountByLabel(mode, aggregations, item.id, getters.activitySelected),
                    );
                    if (!data.every((item) => item === 0)) {
                        datasets.push({
                            label: window.i18n.__(item.label),
                            backgroundColor: item.color ?? '#000000',
                            data: data,
                            borderWith: 0,
                        });
                    }
                }
            } else {
                datasets = [
                    {
                        label: window.i18n.__('Tâche terminée'),
                        backgroundColor: '#826af9',
                        data: intervals.map(getCountByActivity(mode, aggregations, 'task')),
                        borderWidth: 0,
                    },
                    {
                        label: window.i18n.__('Appel passé'),
                        backgroundColor: '#2d99ff',
                        data: intervals.map(getCountByActivity(mode, aggregations, 'phonecall')),
                        borderWidth: 0,
                    },
                    {
                        label: window.i18n.__('Evènement passé'),
                        backgroundColor: '#2cd9c5',
                        data: intervals.map(getCountByActivity(mode, aggregations, 'event')),
                        borderWidth: 0,
                    },
                    {
                        label: window.i18n.__('Email envoyé'),
                        backgroundColor: '#ffe700',
                        data: intervals.map(getCountByActivity(mode, aggregations, 'email')),
                        borderWidth: 0,
                    },
                ];
            }
            commit('setChartData', {
                labels: intervals.map(getLabelByMode(mode)),
                datasets: datasets,
            });
        },
        formatData({ dispatch, getters }, data) {
            const aggregationsBy =
                getters.type === 'label'
                    ? `by_${getters.embed}_and_label`
                    : `by_${getters.embed}_and_event`;
            const mode = `${getters.embed}s`;
            const { start, end } = getters.filters.due_date;

            let aggregations = addTotalActivities(data?.aggregations?.[aggregationsBy] ?? {});
            let intervals = null;

            if (getters.type === 'staff' || getters.type === 'label') {
                aggregations = sortDescBy('total')(aggregations);
                intervals = Object.keys(aggregations);
            } else {
                intervals = enumeratePeriodsBetweenDates(start, end, mode, 'YYYY-MM-DD');
            }

            dispatch('updateChartData', { aggregations, intervals, mode });
            dispatch('updateRows', { aggregations, intervals, mode });
        },
        saveOnBrowser({ state }) {
            localstorage.set({
                filters: state.filters,
                embed: state.embed,
                periodSelected: state.periodSelected,
            });
        },
        loadReportData: debounce(({ commit, dispatch, getters }) => {
            if (getters.type === 'all' && localstorage.get('embed')) {
                commit('setEmbed', localstorage.get('embed').embed);
                commit('setPeriodSelected', localstorage.get('embed').embed);
            }
            const aggregationsBy =
                getters.type === 'label'
                    ? `aggregations.by_${getters.embed}_and_label`
                    : `aggregations.by_${getters.embed}_and_event`;
            const oldType = getters.type;
            return new Promise((resolve, reject) => {
                ApiClient.post(
                    '/activities/crm/search',
                    { embed: [aggregationsBy] },
                    { filters: getters.filters },
                )
                    .then((response) => {
                        if (oldType === getters.type) {
                            dispatch('formatData', response.data);
                            commit('setStatus', 'success');
                            resolve(response);
                        }
                    })
                    .catch((err) => {
                        Push.error(
                            window.i18n.__(
                                'Erreur lors de la récupération des données. Veuillez ré-essayer dans quelques minutes.',
                            ),
                            false,
                        );
                        commit('setStatus', 'error');
                        reject(err);
                    });
            });
        }),
        loadStaffs: ({}, params) => {
            return ApiClient.get('/staffs', params);
        },
        loadAllStaffs: ({ commit, dispatch }) => {
            let datas = [];

            const fetchStaffs = async (offset) => {
                const response = await dispatch('loadStaffs', offset ? { offset } : {});
                const { pagination, data } = response.data;
                datas = [...datas, ...data];
                if (pagination.count === pagination.limit) await fetchStaffs(pagination.offset);
                return datas;
            };

            return new Promise(async (resolve, reject) => {
                await fetchStaffs(null)
                    .then((response) => {
                        commit('setStaffs', response);
                        resolve(response);
                    })
                    .catch((err) => {
                        Push.error(
                            window.i18n.__(
                                'Erreur lors de la récupération des données. Veuillez ré-essayer dans quelques minutes.',
                            ),
                            false,
                        );
                        commit('setStatus', 'error');
                        reject(err);
                    });
            });
        },
        loadActivities: ({ commit, dispatch }) => {
            return new Promise(async (resolve, reject) => {
                await ApiClient.get('/activities/crm/metas')
                    .then((response) => {
                        const datas = {};
                        datas['task'] = response.data.task.labels;
                        datas['event'] = response.data.event.labels;
                        commit('setActivities', datas);
                        resolve(response);
                    })
                    .catch((err) => {
                        Push.error(
                            window.i18n.__(
                                'Erreur lors de la récupération des données. Veuillez ré-essayer dans quelques minutes.',
                            ),
                            false,
                        );
                        commit('setStatus', 'error');
                        reject(err);
                    });
            });
        },
    },
};
